// Quote API
import {
    RequestConfigFunction,
} from 'app/types/request.types'

import {
    RequestKeys,
} from 'app/hooks/useRequest'

type QuoteRequests = {
    domainName: string,
    uniqField: string,
    url: {
        upcoming: string,
        toBeCompleted: string,
        completed: string,
    },
    get: RequestConfigFunction<{ id: number }>,
    [RequestKeys.getAQuotes]: RequestConfigFunction<{ params?: Record<string, any> }>,
    update: RequestConfigFunction<{ id: number }>,
    create: RequestConfigFunction<{ data: any }>,
    modifications: RequestConfigFunction<{ id: number }>,
    archive: RequestConfigFunction<{ id: number }>,
    restore: RequestConfigFunction<{ id: number }>,
    updateRequestedLane: RequestConfigFunction<{ id: number, data: any }>,
    assignToContractBasis: RequestConfigFunction<{ id: number }>,
    getLanesToBeGrouped: RequestConfigFunction<{ id: number }>,
    getLanesToAwardReject: RequestConfigFunction<{ id: number, params?: Record<string, any> }>,
    getLaneGroupsPricingAssignment: RequestConfigFunction<{ id: number }>,
    [RequestKeys.getLaneGroupPricingAssessmentById]:
        RequestConfigFunction<{ id: number, quoteId: number }>,
    getQuoteDocuments: RequestConfigFunction<{ id: number }>,
    removeQuoteDocument: RequestConfigFunction<{ id: number, quoteDocumentId: number }>,
    addQuotePdf: RequestConfigFunction<{ quoteId: number, data: { id: number } }>,
    getLanesByQuote: RequestConfigFunction<{ params?: Record<string, any> }>,
    quotePDFPreview: RequestConfigFunction<undefined>,
    removeQuotePdf: RequestConfigFunction<{
        quoteId: number,
        fileId: string
    }>,
    updatePdfQuoteDescription: RequestConfigFunction<{
        quoteId: number,
        documentId: number,
        description: string
    }>,
    pricingBeingPriced: RequestConfigFunction<Record<string, any>>,
    pricingCompleted: RequestConfigFunction<Record<string, any>>,
    newQuotes: RequestConfigFunction<Record<string, any>>,
}

const quoteRequests: QuoteRequests = {
    domainName: 'Quote',
    uniqField: 'quoteNumber',
    url: {
        upcoming: 'quote/filter-request/kamdashboard-upcoming-quotes',
        toBeCompleted: 'quote/filter-request/kamdashboard-to-be-completed',
        completed: 'quote/filter-request/kamdashboard-completed',
    },
    get: ({
        id,
    }) => {
        return {
            url: `quote/${id}`,
            method: 'GET',
        }
    },
    getAQuotes: ({
        params = {},
    } = {}) => {
        return {
            url: 'quote/filter-request/all',
            method: 'POST',
            ...params,
        }
    },
    update: ({
        id,
    }) => {
        return {
            url: `quote/${id}`,
            method: 'PATCH',
        }
    },
    create: ({
        data,
    }) => {
        return {
            url: 'quote',
            method: 'POST',
            data,
        }
    },
    modifications: ({
        id,
    }) => {
        return {
            url: `quote/${id}/modifications`,
            method: 'GET',
        }
    },
    archive: ({
        id,
    }) => {
        return {
            url: `quote/${id}/archive`,
            method: 'PATCH',
        }
    },
    restore: ({
        id,
    }) => {
        return {
            url: `quote/${id}/restore`,
            method: 'PATCH',
        }
    },
    updateRequestedLane: ({
        id,
        data,
    }) => {
        return {
            url: `quote/${id}/requestedlane`,
            method: 'POST',
            data,
        }
    },
    assignToContractBasis: ({
        id,
    }) => {
        return {
            url: `quote/${id}/assignment-to-contractbasis`,
            method: 'PATCH',
        }
    },
    getLanesToBeGrouped: ({
        id,
    }) => {
        return {
            url: `quote/${id}/lane/to-be-grouped`,
            method: 'GET',
        }
    },
    getLanesToAwardReject: ({
        id,
        params,
    }) => {
        return {
            url: `quote/${id}/lane/filter-request/awardreject`,
            method: 'POST',
            ...params,
        }
    },
    getLaneGroupsPricingAssignment: ({
        id,
    }) => {
        return {
            url: `quote/${id}/lanegroup/filter-request/pricing-assignment`,
            method: 'POST',
        }
    },
    [RequestKeys.getLaneGroupPricingAssessmentById]: ({
        id,
        quoteId,
    }) => {
        return {
            url: `/quote/${quoteId}/lanegroup/${id}/pricing-assignment`,
            method: 'GET',
        }
    },
    getQuoteDocuments: ({
        id,
    }) => {
        return {
            url: `quote/${id}/quotedocument/filter-request/all`,
            method: 'POST',
        }
    },
    removeQuoteDocument: ({
        id,
        quoteDocumentId,
    }) => {
        return {
            url: `quote/${id}/quotedocument/filter-request/${quoteDocumentId}/remove`,
            method: 'POST',
        }
    },
    addQuotePdf:
        ({
            quoteId,
            data,
        }) => {
            return {
                url: `quote/${quoteId}/quotedocument/existing-file`,
                data,
                method: 'POST',
            }
        },
    getLanesByQuote: ({
        params = {},
    } = {}) => {
        return {
            url: 'lane/filter-request/quote-stepper',
            method: 'POST',
            ...params,
        }
    },
    quotePDFPreview: () => {
        return {
            url: 'quote/quotedocument/preview/pdf',
            method: 'POST',
        }
    },
    removeQuotePdf: ({
        quoteId,
        fileId,
    }) => {
        return {
            url: `quote/${quoteId}/quotedocument/${fileId}/removal`,
            method: 'POST',
        }
    },
    updatePdfQuoteDescription: ({
        quoteId, documentId, description,
    }) => {
        return {
            url: `quote/${quoteId}/quotedocument/${documentId}/description`,
            method: 'PATCH',
            data: {
                description,
            },
        }
    },
    pricingBeingPriced: (params = {}) => {
        return {
            url: 'quote/filter-request/pricing-being-priced',
            method: 'POST',
            ...params,
        }
    },
    pricingCompleted: (params = {}) => {
        return {
            url: 'quote/filter-request/pricing-completed',
            method: 'POST',
            ...params,
        }
    },
    newQuotes: (params = {}) => {
        return {
            url: 'quote/filter-request/pricing-created',
            method: 'POST',
            ...params,
        }
    },
}

export default quoteRequests
